import {
    Box,
    Chip,
    ClickAwayListener,
    IconButton,
    Paper,
    Popper,
} from '@mui/material';
import type { VirtualElement } from '@popperjs/core';
import { Close } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

/**
 * Gap between a side panel and what it stands beside: the sidebar, its
 * anchor, or the panel an editor opens beside. The same gap a menu keeps
 * from its control.
 */
export const SIDE_PANEL_GAP_PX = 4;
/** Distance a side panel keeps from the window's edge, level with the sidebar's. */
export const WINDOW_INSET_PX = 8;
const PANEL_WIDTH_PX = 380;

/**
 * MUI renders Menus, Selects and Dialogs into a portal under `.MuiModal-root`.
 * A click or an Escape inside one belongs to it, not to the panel beneath.
 */
const PORTAL_OVERLAY_SELECTOR = '.MuiModal-root';

/** Whether a click or key event landed inside a portaled overlay. */
const inOverlay = (target: EventTarget | null) =>
    target instanceof Element &&
    target.closest(PORTAL_OVERLAY_SELECTOR) !== null;

/** Where the panel stands from its anchor, per alignment. */
const PLACEMENT = {
    bottom: 'right-end',
    above: 'top-start',
} as const;

/** Attribute on a side panel's paper: an editor opened from the panel opens beside it. */
export const SIDE_PANEL_ATTRIBUTE = 'data-properties-panel';

/**
 * The right edge a panel or menu beside the sidebar stands off from: the
 * sidebar's that holds `el`, or `el`'s own when it is not in a sidebar.
 */
function sidebarRightEdge(el: HTMLElement): number {
    return (el.closest('[data-workflow-sidebar]') ?? el).getBoundingClientRect()
        .right;
}

/**
 * The point a panel or menu beside the sidebar hangs from: one gap right of
 * the sidebar that holds `el` (or of `el`, outside a sidebar), and the
 * window inset above the window's bottom.
 */
export function besideSidebarPoint(el: HTMLElement): {
    left: number;
    top: number;
} {
    return {
        left: sidebarRightEdge(el) + SIDE_PANEL_GAP_PX,
        top: window.innerHeight - WINDOW_INSET_PX,
    };
}

/**
 * A panel that shows properties: a header with an optional badge, a title
 * and a close button, then a body that scrolls within the window.
 * `align="bottom"` stands the panel beside the sidebar, one gap above the
 * window's bottom; `align="above"` stands it one gap above `anchorEl`, its
 * left edge on the anchor's, for a control outside the sidebar.
 * Escape and a click outside close it, except where a menu or dialog opened
 * from it takes them, or something else already handled the Escape.
 */
export default function PropertiesSidePanel({
    anchorEl,
    align,
    badge,
    title,
    onClose,
    'data-testid': testId,
    children,
}: {
    /** The element beside or above which the panel opens. */
    anchorEl: HTMLElement | null;
    align: 'bottom' | 'above';
    badge?: string;
    title: ReactNode;
    onClose: () => void;
    'data-testid'?: string;
    children: ReactNode;
}) {
    const intl = useIntl();
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.key === 'Escape' &&
                !event.defaultPrevented &&
                !inOverlay(event.target)
            ) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);
    // Read at every placement, so the panel follows a resized sidebar or
    // window.
    const bottomAnchor = useMemo<VirtualElement | null>(
        () =>
            anchorEl && {
                getBoundingClientRect: () =>
                    DOMRect.fromRect({
                        x: sidebarRightEdge(anchorEl),
                        y: besideSidebarPoint(anchorEl).top,
                        width: 0,
                        height: 0,
                    }),
            },
        [anchorEl]
    );

    return (
        <Popper
            open={anchorEl !== null}
            anchorEl={align === 'bottom' ? bottomAnchor : anchorEl}
            placement={PLACEMENT[align]}
            modifiers={[
                {
                    name: 'offset',
                    options: {
                        offset: [0, SIDE_PANEL_GAP_PX],
                    },
                },
            ]}
            sx={{ zIndex: (theme) => theme.zIndex.modal }}
        >
            <ClickAwayListener
                onClickAway={(event) => {
                    // A click on the page's body is no click on anything.
                    if (
                        (event.target as Element | null)?.localName !==
                            'body' &&
                        !inOverlay(event.target)
                    ) {
                        onClose();
                    }
                }}
            >
                <Paper
                    elevation={0}
                    data-testid={testId}
                    {...{ [SIDE_PANEL_ATTRIBUTE]: '' }}
                    sx={(theme) => ({
                        display: 'flex',
                        flexDirection: 'column',
                        width: PANEL_WIDTH_PX,
                        // A long list scrolls inside the panel, which stays
                        // within the window.
                        maxHeight: `calc(100vh - ${2 * WINDOW_INSET_PX}px)`,
                        backgroundColor: theme.palette.background.raised,
                        backgroundImage: 'none',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: `${theme.shape.borderRadius}px`,
                        boxShadow: theme.mixins.frostedSurface.boxShadow,
                    })}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        py={1}
                        pr={1}
                        pl={2}
                        borderBottom={1}
                        borderColor="divider"
                    >
                        {badge && (
                            <Chip
                                variant="tag"
                                label={badge}
                                sx={{ flexShrink: 0 }}
                            />
                        )}
                        <Typography
                            component="span"
                            flex={1}
                            minWidth={0}
                            fontSize={14}
                            fontWeight={500}
                            noWrap
                        >
                            {title}
                        </Typography>
                        <IconButton
                            onClick={onClose}
                            aria-label={intl.formatMessage({ id: 'close' })}
                            data-testid="close-properties-panel"
                            sx={{ width: 28, height: 28 }}
                        >
                            <Close sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={2}
                        p={2}
                        overflow="auto"
                    >
                        {children}
                    </Box>
                </Paper>
            </ClickAwayListener>
        </Popper>
    );
}
